import type { ITenantDestination } from '@/entities/tenant-destination'
import type { LanguageOption } from '@/shared/hooks/use-language-filter'
import type { ITourPoi } from '@/shared/api/types/tour-types'
import type { TourRouteCoordinate } from '@/features/tour/tour-route-map-utils'
import type { useTourPois } from './use-tour-pois'
import { useTranslation } from 'react-i18next'
import { TourMapPreview } from '@/features/tour/tour-map-preview'
import { TourPoiOrderList } from '@/features/tour/tour-poi-order-list'
import { TourPoiSelector } from '@/features/tour/tour-poi-selector'
import { cn } from '@/shared/libs/cn'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { SectionSaveButton } from '@/shared/ui/section-save-button'

interface PoisCardProps {
  isEdit: boolean
  isFormReady: boolean
  selectedPois: ITourPoi[]
  routeCoordinates: TourRouteCoordinate[]
  onSelectedPoisChange: (next: ITourPoi[]) => void
  onRouteCoordinatesChange: (next: TourRouteCoordinate[]) => void
  selectedTenantDestination: ITenantDestination | undefined
  /** POI picker state and actions (see `useTourPois`). */
  poiPicker: ReturnType<typeof useTourPois>
  languageOptions: LanguageOption[]
  languageCode: string
  onLanguageChange: (code: string) => void
  isPoisBatchDirty: boolean
  onSavePois: () => void
  isCoordinatesDirty: boolean
  isSavingCoordinates: boolean
  isUpdating: boolean
  onSaveCoordinates: () => void
}

/** POI picker, ordered list of the tour's POIs and the route map. */
export function PoisCard({
  isEdit,
  isFormReady,
  selectedPois,
  routeCoordinates,
  onSelectedPoisChange,
  onRouteCoordinatesChange,
  selectedTenantDestination,
  poiPicker,
  languageOptions,
  languageCode,
  onLanguageChange,
  isPoisBatchDirty,
  onSavePois,
  isCoordinatesDirty,
  isSavingCoordinates,
  isUpdating,
  onSaveCoordinates,
}: PoisCardProps) {
  const { t } = useTranslation()
  const isPickerLoading =
    poiPicker.isPoisLoading || (poiPicker.isPoisFetching && !poiPicker.isPoisFetchingNextPage)
  const isPickerDisabled = !isFormReady || (isEdit && poiPicker.isPoiActionPending)

  return (
    <FormSectionCard
      title={t('tour.pois')}
      action={
        isEdit && (
          <SectionSaveButton
            disabled={poiPicker.isSavingPoisBatch || !isPoisBatchDirty}
            saving={poiPicker.isSavingPoisBatch}
            onSave={onSavePois}
            label={t('tour.savePoisChanges', { defaultValue: 'Lưu thay đổi POI' })}
          />
        )
      }
    >
      <div className={cn('space-y-5', !isFormReady && 'opacity-50 pointer-events-none')}>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6">
          <div className="lg:col-span-6">
            <p className="mb-3 text-sm font-semibold text-zinc-800">{t('tour.selectPois')}</p>
            <TourPoiSelector
              pois={poiPicker.pois}
              selectedPoiIds={selectedPois.map((item) => item.poiId)}
              disabled={isPickerDisabled}
              isLoading={isPickerLoading}
              isFetchingNextPage={poiPicker.isPoisFetchingNextPage}
              hasNextPage={poiPicker.poisHasNextPage}
              onLoadMore={() => void poiPicker.fetchNextPoisPage()}
              languageOptions={languageOptions}
              selectedLanguageCode={languageCode}
              onLanguageChange={onLanguageChange}
              onQueryFiltersChange={poiPicker.setQueryFilters}
              onAdd={poiPicker.addPoi}
              onAddMany={poiPicker.addPoisMany}
            />
          </div>
          <div className="lg:col-span-6">
            <p className="mb-3 text-sm font-semibold text-zinc-800">
              {t('tour.selectedPois')}
              <span className="ml-1.5 font-normal tabular-nums text-zinc-500">
                ({selectedPois.length})
              </span>
            </p>
            <TourPoiOrderList
              selectedPois={selectedPois}
              poiMap={poiPicker.poiMap}
              languageCode={languageCode}
              onReorderMove={onSelectedPoisChange}
              onRemoveSelected={onSelectedPoisChange}
              outOfScopePoiIds={poiPicker.outOfScopePoiIds}
              isProcessing={isPickerDisabled}
            />
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-zinc-800">{t('tour.mapPreview')}</p>
            {isEdit && (
              <SectionSaveButton
                disabled={isUpdating || !isCoordinatesDirty}
                saving={isSavingCoordinates}
                onSave={onSaveCoordinates}
              />
            )}
          </div>
          <TourMapPreview
            selectedPois={selectedPois}
            poiMap={poiPicker.poiMap}
            coordinates={routeCoordinates}
            destinationAreaBoundary={selectedTenantDestination?.areaBoundary ?? null}
            destinationCenterLat={selectedTenantDestination?.centerLat ?? null}
            destinationCenterLng={selectedTenantDestination?.centerLng ?? null}
            editable
            onCoordinatesChange={onRouteCoordinatesChange}
          />
        </div>
      </div>
    </FormSectionCard>
  )
}
